import m from 'mithril';

export const FluidBackground = {
    oncreate: (vnode) => {
        const canvas = vnode.dom;
        const gl = canvas.getContext("webgl");
        if (!gl) return;

        const startTime = Date.now();

        const resize = () => {
            if (!canvas) return;
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
            gl.viewport(0, 0, canvas.width, canvas.height);
        };
        window.addEventListener("resize", resize);
        resize();

        const vs = `attribute vec2 p; void main(){gl_Position=vec4(p,0,1);}`;

        const fs = `
            precision highp float;
            uniform float t;
            uniform vec2 r;
            uniform vec2 u_mouse;

            // Random hash function
            float hash21(vec2 p) {
                return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453);
            }

            void main() {
                float aspect = r.y/r.x;
                vec2 uv = gl_FragCoord.xy / r.x;
                uv -= vec2(0.5, 0.5*aspect);
                
                // Mouse interaction
                vec2 mouse = u_mouse / r.x;
                mouse -= vec2(0.5, 0.5*aspect);
                
                // Scale for hexagons
                uv *= 20.0;
                mouse *= 20.0;
                
                // Hexagonal Grid Logic
                const vec2 s = vec2(1.0, 1.7320508); // 1, sqrt(3)
                vec4 h = vec4(0.0, s.y, s.x, s.y*0.5);
                
                vec2 a = mod(uv, s) - s*0.5;
                vec2 b = mod(uv - s*0.5, s) - s*0.5;
                
                vec2 gv = dot(a, a) < dot(b, b) ? a : b;
                vec2 id = uv - gv;
                
                // Random value per cell
                float rnd = hash21(id);
                
                // Distance from screen center to hex cell center
                float distToCenter = length(id);
                
                // Distance from mouse to hex cell center
                float distToMouse = length(id - mouse);
                
                // Mouse influence (creates a wave/ripple around the mouse)
                float mouseInfluence = smoothstep(6.0, 0.0, distToMouse);
                
                // Hexagon SDF (distance from center of hex)
                vec2 p = abs(gv);
                float hexDist = max(dot(p, s*0.5), p.x);
                
                // Convert to "distance from edge" (0 at edge, 1 at center)
                float dist = 1.0 - (hexDist * 2.0);
                dist = clamp(dist, 0.0, 1.0);
                
                // Animation Logic (Ripple)
                // Add mouse influence to the edge calculation to make cells react
                float edge = (t * 0.1 + rnd * 10.0 - distToCenter * 0.05 - mouseInfluence * 0.5) * 0.5;
                edge = 2.0 * fract(edge * 0.5);
                
                // Pattern logic
                float value = fract(dist * 2.0);
                value = mix(value, 1.0 - value, step(1.0, edge));
                
                edge = pow(abs(1.0 - edge), 2.0);
                value = smoothstep(edge - 0.05, edge, 0.95 * value);
                
                // Slight fade based on distance from center
                value += distToCenter * 0.02;
                
                // Colors
                vec4 bg = vec4(1.0, 1.0, 1.0, 1.0);
                
                // Varied color palette (Pastel Rainbow)
                // Shift colors slightly based on mouse proximity
                vec3 cols = 0.5 + 0.5 * cos(vec3(0.0, 0.33, 0.67) * 6.28 + length(id) * 0.2 + t * 0.2 + rnd - mouseInfluence * 0.5);
                
                // Make colors lighter/whiter (Pastel)
                cols = mix(vec3(1.0), cols, 0.5);
                
                vec4 fg = vec4(cols, 1.0);
                
                vec4 finalColor = mix(bg, fg, value);
                
                // Increase opacity slightly where the mouse is
                float finalOpacity = mix(0.05, 0.10, mouseInfluence);
                finalColor = mix(vec4(1.0), finalColor, finalOpacity); 

                gl_FragColor = finalColor;
            }
        `;

        const createShader = (type, src) => {
            const s = gl.createShader(type);
            gl.shaderSource(s, src);
            gl.compileShader(s);
            if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
                console.warn(gl.getShaderInfoLog(s));
                return null;
            }
            return s;
        };

        const program = gl.createProgram();
        const vShader = createShader(gl.VERTEX_SHADER, vs);
        const fShader = createShader(gl.FRAGMENT_SHADER, fs);

        if (!vShader || !fShader) return;

        gl.attachShader(program, vShader);
        gl.attachShader(program, fShader);
        gl.linkProgram(program);
        gl.useProgram(program);

        const buffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1,1,-1,-1,1,-1,1,1,-1,1,1]), gl.STATIC_DRAW);

        const pLoc = gl.getAttribLocation(program, "p");
        gl.enableVertexAttribArray(pLoc);
        gl.vertexAttribPointer(pLoc, 2, gl.FLOAT, false, 0, 0);

        const tLoc = gl.getUniformLocation(program, "t");
        const rLoc = gl.getUniformLocation(program, "r");
        const mLoc = gl.getUniformLocation(program, "u_mouse");

        let mouseX = window.innerWidth / 2;
        let mouseY = window.innerHeight / 2;

        const onMouseMove = (e) => {
            mouseX = e.clientX;
            mouseY = window.innerHeight - e.clientY; // WebGL Y is up
        };
        window.addEventListener("mousemove", onMouseMove);

        let frameId;

        const loop = () => {
            if (!canvas || !gl) return;
            const now = (Date.now() - startTime) * 0.001;
            gl.uniform1f(tLoc, now);
            gl.uniform2f(rLoc, canvas.width, canvas.height);
            gl.uniform2f(mLoc, mouseX, mouseY);
            gl.drawArrays(gl.TRIANGLES, 0, 6);
            frameId = requestAnimationFrame(loop);
        };
        loop();

        vnode.state.onremove = () => {
            cancelAnimationFrame(frameId);
            window.removeEventListener("resize", resize);
            window.removeEventListener("mousemove", onMouseMove);
        };
    },
    onremove: (vnode) => {
        if (vnode.state.onremove) vnode.state.onremove();
    },
    view: () => m("canvas", { class: "canvas-bg" })
};
